/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.org/docs/browser-apis/
 */

import React from 'react'
import LazyCrmAppShell from './src/components/CrmAppShell/LazyCrmAppShell'
import { consumeMagicLinkFromLocation } from './src/utils/authSession'
import {
  ensureDashboardDiagnosticsPrefetch,
} from './src/utils/dashboardDiagnosticsPrefetch'
import { prefetchMessagePage } from './src/utils/prefetchMessagePage'
import { prefetchReservationTracker } from './src/utils/openReservationInNewTab'
import { isCrmPath } from './src/utils/crmPaths'

function isReservationPath(pathname = '') {
  return pathname === '/reservation' || pathname.startsWith('/reservation/')
}

// Store the last scroll position
let lastScrollPosition = 0
let isQueryChange = false

export const onClientEntry = () => {
  // Do not warm tracker/message chunks here — that fights the homepage (and
  // other public pages) in develop and surfaces as chunk-load / HMR timeouts.
  // Invite links can land before React mounts; apply session ASAP.
  consumeMagicLinkFromLocation()

  // Develop: webpack's default chunk timeout races the reservation/dashboard
  // compile. Catch it so Gatsby does not overlay "Unhandled Runtime Errors",
  // then retry the same navigation once.
  if (process.env.NODE_ENV !== 'development') return
  if (typeof window === 'undefined') return
  window.addEventListener('unhandledrejection', (event) => {
    const msg = String(event?.reason?.message || event?.reason || '')
    if (!/Loading chunk .+ failed/i.test(msg)) return
    event.preventDefault()
    if (window.__doggosChunkReload) return
    window.__doggosChunkReload = true
    window.location.reload()
  })
}

export const onInitialClientRender = () => {
  lastScrollPosition = window.scrollY
  const path = window.location?.pathname || ''
  if (isCrmPath(path)) {
    ensureDashboardDiagnosticsPrefetch()
    prefetchMessagePage()
  }
  // Already on /reservation — do not prefetch the same chunk (that is what
  // times out webpack: page load + warm fetch compiling together).
}

export const onPreRouteUpdate = ({ location, prevLocation }) => {
  isQueryChange =
    location.pathname === prevLocation?.pathname &&
    location.search !== prevLocation?.search

  if (isCrmPath(location.pathname)) {
    ensureDashboardDiagnosticsPrefetch()
    prefetchMessagePage()
  } else if (isReservationPath(location.pathname)) {
    prefetchReservationTracker(
      location.pathname.split('/')[2] || 'warm',
    )
  }
}

export const onRouteUpdate = ({ location, prevLocation }) => {
  // SPA navigations to /?doggos_l=… (invite click while already signed in)
  // must swap the session — AuthProvider's mount effect alone misses these.
  if (location?.search?.includes('doggos_l=')) {
    consumeMagicLinkFromLocation()
  }

  // Only handle scroll behavior for the search page
  if (location.pathname === '/search/') {
    // If we're on the search page and only query params changed, prevent scroll
    if (
      location.pathname === prevLocation?.pathname &&
      location.search !== prevLocation?.search
    ) {
      // Prevent default scroll behavior
      if (typeof window !== 'undefined') {
        window.history.scrollRestoration = 'manual'
      }
      return
    }
  }

  // For actual page navigations, scroll to top
  if (
    location.pathname !== prevLocation?.pathname &&
    !window.history.state?.key
  ) {
    window.scrollTo(0, 0)
    lastScrollPosition = 0
  }

  // Reset the query change flag
  isQueryChange = false
}

export const shouldUpdateScroll = ({
  routerProps,
  prevRouterProps,
  getSavedScrollPosition,
}) => {
  // For the search page, don't scroll when only query params change
  if (routerProps?.location?.pathname === '/search/') {
    if (
      routerProps?.location?.pathname === prevRouterProps?.location?.pathname &&
      routerProps?.location?.search !== prevRouterProps?.location?.search
    ) {
      return false
    }
  }

  // For back/forward navigation between pages
  if (window.history.action === 'POP') {
    const currentPosition = getSavedScrollPosition(routerProps.location) || [
      0, 0,
    ]
    window.scrollTo({
      top: currentPosition[1],
      left: currentPosition[0],
      behavior: 'instant',
    })
    return false
  }

  // For other cases, let Gatsby handle scroll behavior
  return true
}

/** CRM shell loads async only on CRM routes — keeps homepage commons light. */
export const wrapPageElement = ({ element, props }) => (
  <LazyCrmAppShell location={props.location}>{element}</LazyCrmAppShell>
)
